import { useCallback, useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  CONVERSATION_MESSAGE_PAGE_SIZE,
  isThinkingLevel,
  type AgentDefinition,
  type AgentWorkspaceApproval,
  type GenerationParameters,
  type ModelPreferences,
  type Project,
} from "@lumen/shared";
import type { ComposerActivity } from "../../components/composer/Composer";
import { api } from "../../api";
import { useIdleConversationSync } from "./useIdleConversationSync";
import { conversationFollowTarget } from "./conversationQueueState";
import { observeConversationScroll } from "./conversationScroll";
import { rememberConversationTitle } from "../../conversationTitles";
import type { DisplayConversation } from "./ChatPage";
import {
  createConversationStreamEventHandler,
  isAbortError,
  reconnectConversationStream,
  StreamDisconnectedError,
  streamRequest,
  supportsActivity,
} from "./ChatPage.utils";
import type { ChatPageEffectsContext } from "./ChatContextTypes";

const API_BASE = (import.meta.env?.VITE_API_BASE || "/api").replace(/\/$/, "");
const PLACEHOLDER_CONVERSATION_TITLE = "New conversation";
type PersonalizationPreferences = { defaultAgentId?: string | null };

export function useChatPageEffects(context: ChatPageEffectsContext) {
  useIdleConversationSync(context);
  const location = useLocation();
  const projectScoped =
    location.pathname.startsWith("/projects/") ||
    Boolean(context.draftProjectId) ||
    context.conversation?.surface === "project";
  const {
    activeRequest,
    agents,
    agentId,
    applyConversationSnapshot,
    cachedScrollMessage,
    composerActivities,
    conversation,
    conversationRef,
    generationMessageId,
    id,
    draftProjectId,
    initialScrollPending,
    loadingConversation,
    loadingOlderRef,
    onAttention,
    requestedModelId,
    activity,
    thinkingLevel,
    scrollArea,
    scrollContent,
    scrollFrame,
    setActivity,
    setActivityModelSelection,
    setActivitySettings,
    setEnabledActivities,
    setAgentId,
    setAgents,
    setApprovals,
    setCompacting,
    setConversation,
    setDraftComposerDefaults,
    setDevelopmentConfiguration,
    setDevelopmentConfigured,
    setDevelopmentOpen,
    setError,
    setLoadingConversation,
    setModelId,
    setThinkingLevel,
    loadModels,
    setPreferences,
    setSending,
    setShowScrollTop,
    setUsageRevision,
    activityModelIds,
    stickToBottom,
    sending,
    temporary,
    user,
  } = context;
  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation, conversationRef]);
  useEffect(() => {
    if (!temporary) return;
    const discard = () => {
      const current = conversationRef.current;
      if (current?.temporary)
        void fetch(`${API_BASE}/conversations/${current.id}`, {
          method: "DELETE",
          credentials: "include",
          keepalive: true,
        });
    };
    window.addEventListener("pagehide", discard);
    return () => {
      window.removeEventListener("pagehide", discard);
      discard();
    };
  }, [conversationRef, temporary]);
  useEffect(() => {
    setDraftComposerDefaults(undefined);
    // Agent ids available in this environment, captured so a project draft
    // can only adopt a default agent the user actually has access to.
    let availableAgentIds: string[] = [];
    const base = Promise.all([
      loadModels(),
      api<ModelPreferences>("/me/model-preferences"),
      api<AgentDefinition[]>("/agents/available"),
      api<PersonalizationPreferences>("/me/personalization-preferences"),
    ])
      .then(([rows, nextPreferences, agentRows, personalization]) => {
        const safeRows = Array.isArray(rows) ? rows : [];
        const safeAgentRows = Array.isArray(agentRows) ? agentRows : [];
        setAgents(safeAgentRows);
        if ((!id || temporary) && safeAgentRows.length) {
          const savedAgentId = projectScoped
            ? null
            : localStorage.getItem("lumen:active-agent");
          setAgentId(
            savedAgentId &&
              safeAgentRows.some((agent) => agent.id === savedAgentId)
              ? savedAgentId
              : personalization.defaultAgentId &&
                  safeAgentRows.some(
                    (agent) => agent.id === personalization.defaultAgentId,
                  )
                ? personalization.defaultAgentId
                : (safeAgentRows.find((agent) => agent.slug === "chat")?.id ??
                  safeAgentRows[0].id),
          );
        }
        setPreferences(nextPreferences);
        if ((!id || temporary) && safeRows.length) {
          const chatModels = safeRows.filter((model) =>
            supportsActivity(model, "ask"),
          );
          const eligible = chatModels.length ? chatModels : safeRows;
          if (!chatModels.length) {
            const fallbackActivity = (
              [
                "image",
                "audio",
                "video",
                "speech",
                "transcription",
                "model3d",
                "sprite",
                "tileset",
              ] as const
            ).find((candidate) =>
              safeRows.some((model) => supportsActivity(model, candidate)),
            );
            if (fallbackActivity) setActivity(fallbackActivity);
          }
          const preferred =
            requestedModelId ||
            nextPreferences.activityDefaultModelIds?.ask ||
            nextPreferences.defaultModelId ||
            nextPreferences.adminDefaultModelId;
          setModelId(
            preferred && eligible.some((model) => model.id === preferred)
              ? preferred
              : eligible[0].id,
          );
        }
        availableAgentIds = safeAgentRows.map((agent) => agent.id);
      })
      .catch((reason) => setError(reason.message));
    if (!draftProjectId) return;
    // A project draft adopts its project's composer defaults once both the
    // global defaults and the project have resolved, so the project wins.
    // This mirrors how loaded project conversations apply their stored
    // composerDefaults.
    void Promise.all([base, api<Project>(`/projects/${draftProjectId}`)])
      .then(([, project]) => {
        const defaults = project.composerDefaults ?? {};
        const generation = defaults.generationParameters ?? {};
        const nextActivity =
          project.defaultActivity ?? generation.activity ?? "ask";
        setDraftComposerDefaults({
          ...defaults,
          generationParameters: {
            ...generation,
            ...(nextActivity ? { activity: nextActivity } : {}),
          },
        });
        if (
          nextActivity &&
          composerActivities.includes(nextActivity as ComposerActivity)
        )
          setActivity(nextActivity as ComposerActivity);
        setActivitySettings(generation.activitySettings ?? {});
        setEnabledActivities(["ask", ...(generation.enabledActivities ?? [])]);
        setThinkingLevel(defaults.thinkingLevel ?? "");
        if (defaults.agentId && availableAgentIds.includes(defaults.agentId))
          setAgentId(defaults.agentId);
      })
      .catch(() => {
        // An unknown or inaccessible project id surfaces when the first
        // message is sent instead.
      });
  }, [
    composerActivities,
    draftProjectId,
    id,
    requestedModelId,
    projectScoped,
    setActivity,
    setActivitySettings,
    setAgentId,
    setAgents,
    setEnabledActivities,
    setError,
    setDraftComposerDefaults,
    setModelId,
    loadModels,
    setPreferences,
    setThinkingLevel,
    temporary,
  ]);
  useEffect(() => {
    if (agentId || !agents.length || (id && !temporary)) return;
    const fallback = agents.find((agent) => agent.slug === "chat") ?? agents[0];
    setAgentId(fallback.id);
  }, [agentId, agents, id, setAgentId, temporary]);
  useEffect(() => {
    if (agentId && !projectScoped)
      localStorage.setItem("lumen:active-agent", agentId);
  }, [agentId, projectScoped]);
  useEffect(() => {
    if (!projectScoped) localStorage.setItem("lumen:active-activity", activity);
  }, [activity, projectScoped]);
  useEffect(() => {
    if (!projectScoped)
      localStorage.setItem("lumen:active-thinking-level", thinkingLevel);
  }, [thinkingLevel, projectScoped]);
  const loadApprovals = useCallback(
    () =>
      user.organizationPermissions.includes("agent-workspaces:use")
        ? api<AgentWorkspaceApproval[]>("/agent-workspace-approvals")
            .then(setApprovals)
            .catch(() => undefined)
        : Promise.resolve(),
    [setApprovals, user.organizationPermissions],
  );
  useEffect(() => {
    void loadApprovals();
  }, [loadApprovals, id]);
  useEffect(() => {
    if (
      !id ||
      temporary ||
      !user.organizationPermissions.includes("agent-workspaces:use")
    ) {
      setDevelopmentConfiguration(null);
      setDevelopmentConfigured(false);
      setDevelopmentOpen(false);
      return;
    }
    void api<{
      configured: null | {
        workspaceId: string;
        repositoryId: string | null;
      };
      configurations?: Array<{ repositoryId: string }>;
    }>(`/conversations/${id}/development-workspace`)
      .then((value) => {
        setDevelopmentConfiguration({
          conversationId: id,
          workspaceId: value.configured?.workspaceId ?? null,
          repositoryId: value.configured?.repositoryId ?? null,
        });
        setDevelopmentConfigured(Boolean(value.configured?.workspaceId));
      })
      .catch(() => {
        setDevelopmentConfiguration(null);
        setDevelopmentConfigured(false);
      });
  }, [
    id,
    setDevelopmentConfiguration,
    setDevelopmentConfigured,
    setDevelopmentOpen,
    temporary,
    user.organizationPermissions,
  ]);
  useEffect(() => {
    let cancelled = false;
    // Creating a conversation navigates to its new route before the first
    // message stream starts. Keep that request alive when the route already
    // points at the conversation we just created.
    if (id && !temporary && conversationRef.current?.id === id) {
      setLoadingConversation(false);
      return;
    }
    activeRequest.current?.abort();
    activeRequest.current = null;
    generationMessageId.current = null;
    // The busy indicator belongs to the previously visible conversation.
    // Reset it whenever the route changes so the send/stop button and the
    // per-message controls reflect the newly loaded conversation instead of
    // the previous one's generation status. The load logic below re-asserts
    // sending for a conversation that still has a running message.
    setSending(false);
    setCompacting(false);
    if (!id || temporary) {
      conversationRef.current = null;
      setConversation(null);
      setLoadingConversation(false);
      setError("");
      setShowScrollTop(false);
      stickToBottom.current = true;
      cachedScrollMessage.current = null;
      scrollFrame.current = 0;
      return;
    }
    setLoadingConversation(true);
    conversationRef.current = null;
    setConversation(null);
    setError("");
    setShowScrollTop(false);
    stickToBottom.current = true;
    cachedScrollMessage.current = null;
    scrollFrame.current = 0;
    let resumeController: AbortController | null = null;
    api<DisplayConversation>(
      `/conversations/${id}?limit=${CONVERSATION_MESSAGE_PAGE_SIZE}`,
    )
      .then(async (row) => {
        if (cancelled) return;
        initialScrollPending.current = true;
        const snapshot = applyConversationSnapshot(null, row);
        conversationRef.current = snapshot;
        setConversation(snapshot);
        setModelId(row.modelId ?? "");
        const latestUserMessage = [...(row.messages ?? [])]
          .reverse()
          .find((message) => message.role === "user");
        // Restore the mode from the conversation's last message, mirroring how
        // the agent workspace is restored. New conversations keep the mode
        // saved in localStorage instead; messages recorded before modes existed
        // were plain Ask conversations.
        if (latestUserMessage) {
          const messageActivity =
            latestUserMessage.generationParameters?.activity;
          setActivity(
            messageActivity &&
              composerActivities.includes(messageActivity as ComposerActivity)
              ? (messageActivity as ComposerActivity)
              : "ask",
          );
        } else if (row.surface === "project") {
          const defaults = row.composerDefaults ?? {};
          const generation = (defaults.generationParameters ??
            {}) as GenerationParameters;
          const nextActivity =
            typeof defaults.activity === "string"
              ? defaults.activity
              : generation.activity;
          if (
            nextActivity &&
            composerActivities.includes(nextActivity as ComposerActivity)
          )
            setActivity(nextActivity as ComposerActivity);
          if (generation.activitySettings)
            setActivitySettings(generation.activitySettings);
          if (Array.isArray(generation.enabledActivities))
            setEnabledActivities(["ask", ...generation.enabledActivities]);
          if (isThinkingLevel(defaults.thinkingLevel))
            setThinkingLevel(defaults.thinkingLevel);
        }
        if (latestUserMessage?.generationParameters?.activityModelIds)
          Object.assign(
            activityModelIds.current,
            latestUserMessage.generationParameters.activityModelIds,
          );
        if (latestUserMessage?.generationParameters?.activityModelIds)
          setActivityModelSelection((current) => ({
            ...current,
            ...latestUserMessage.generationParameters?.activityModelIds,
          }));
        if (latestUserMessage?.generationParameters?.activitySettings)
          setActivitySettings((current) => ({
            ...current,
            ...latestUserMessage.generationParameters?.activitySettings,
          }));
        setAgentId(latestUserMessage?.agentId ?? row.agentId ?? "");
        const runningMessageId = conversationFollowTarget(row);
        if (runningMessageId) {
          generationMessageId.current = runningMessageId;
          setSending(true);
          resumeController = new AbortController();
          activeRequest.current = resumeController;
          const onEvent = createConversationStreamEventHandler({
            conversationId: id,
            setConversation,
            assistantMessageId: generationMessageId,
            applyConversationSnapshot,
            setUsageRevision,
            setError,
          });
          void streamRequest(
            `/conversations/${id}/messages/stream?messageId=${encodeURIComponent(runningMessageId)}`,
            undefined,
            onEvent,
            resumeController.signal,
            {
              isStale: () => conversationRef.current?.id !== id,
              onAttention,
              onDegraded: () =>
                setError(
                  "Live updates are temporarily unavailable. The generation continues in the background.",
                ),
              onRecovered: () => setError(""),
              notifyOnDone: true,
              reconnect: (assistantId) =>
                reconnectConversationStream(
                  id,
                  assistantId ?? runningMessageId,
                ),
            },
          )
            .catch((reason) => {
              if (!isAbortError(reason) && !cancelled)
                setError(
                  reason instanceof StreamDisconnectedError
                    ? "Live updates are temporarily unavailable. The generation continues in the background."
                    : reason instanceof Error
                      ? reason.message
                      : "Could not follow generation",
                );
            })
            .finally(() => {
              if (activeRequest.current === resumeController)
                activeRequest.current = null;
              if (!cancelled) setSending(false);
            });
        }
      })
      .catch((reason) => {
        if (!cancelled)
          setError(
            reason instanceof StreamDisconnectedError
              ? "Live updates are temporarily unavailable. The generation continues in the background."
              : reason.message,
          );
      })
      .finally(() => {
        if (!cancelled) setLoadingConversation(false);
      });
    return () => {
      cancelled = true;
      resumeController?.abort();
      // When `send()` creates a new conversation it navigates to the new
      // route *before* the response stream starts and sets
      // `conversationRef.current` to the new conversation first. In that
      // case this effect is torn down for a route that the send is already
      // streaming to, so the in-flight request belongs to the new route and
      // must be preserved (the same guard the effect body uses above).
      // Only abort requests that belong to the conversation this effect was
      // managing.
      if (conversationRef.current?.id === id) activeRequest.current?.abort();
    };
  }, [
    activeRequest,
    applyConversationSnapshot,
    cachedScrollMessage,
    composerActivities,
    conversationRef,
    generationMessageId,
    activityModelIds,
    id,
    initialScrollPending,
    onAttention,
    scrollFrame,
    setActivity,
    setActivityModelSelection,
    setActivitySettings,
    setAgentId,
    setCompacting,
    setConversation,
    setError,
    setEnabledActivities,
    setLoadingConversation,
    setModelId,
    setSending,
    setShowScrollTop,
    setThinkingLevel,
    setUsageRevision,
    stickToBottom,
    temporary,
  ]);
  // The server generates the conversation title asynchronously while the
  // first answer streams, and a snapshot can arrive before that generation
  // has persisted. When a conversation that already has content still shows
  // the placeholder, poll the server until a real title appears so the
  // header converges to it without requiring a manual refresh.
  useEffect(() => {
    const conversationId = conversation?.id ?? null;
    if (
      !conversationId ||
      conversation?.title !== PLACEHOLDER_CONVERSATION_TITLE ||
      (conversation?.messages?.length ?? 0) === 0
    )
      return;
    let stopped = false;
    const deadline = Date.now() + 120_000;
    const tick = async () => {
      if (stopped) return;
      try {
        const latest = await api<DisplayConversation>(
          `/conversations/${conversationId}?limit=1`,
        );
        if (stopped || conversationRef.current?.id !== conversationId) return;
        if (latest.title !== PLACEHOLDER_CONVERSATION_TITLE)
          setConversation((current) =>
            current?.id === conversationId &&
            current.title === PLACEHOLDER_CONVERSATION_TITLE
              ? { ...current, title: latest.title }
              : current,
          );
      } catch {
        // A transient failure should not stop the watcher.
      }
      if (!stopped && Date.now() < deadline) window.setTimeout(tick, 1_000);
    };
    void tick();
    return () => {
      stopped = true;
    };
  }, [
    conversation?.id,
    conversation?.title,
    conversation?.messages?.length,
    conversationRef,
    setConversation,
  ]);
  // Share any real title the chat page learns (initial load, stream
  // snapshots, the watcher above, manual rename) with the sidebar so it never
  // regresses to the placeholder when the server response is still mid-flight.
  useEffect(() => {
    if (conversation?.id && conversation.title)
      rememberConversationTitle(conversation.id, conversation.title);
  }, [conversation?.id, conversation?.title]);
  useLayoutEffect(() => {
    const area = scrollArea.current;
    if (!area || loadingConversation || loadingOlderRef.current) return;
    if (initialScrollPending.current && stickToBottom.current) {
      area.scrollTop = area.scrollHeight;
      return;
    }
    if (stickToBottom.current) area.scrollTop = area.scrollHeight;
  }, [
    conversation?.messages,
    initialScrollPending,
    loadingConversation,
    loadingOlderRef,
    scrollArea,
    sending,
    stickToBottom,
  ]);
  useEffect(() => {
    const area = scrollArea.current;
    const content = scrollContent.current;
    if (!area || !content || typeof ResizeObserver === "undefined") return;
    return observeConversationScroll(
      area,
      content,
      initialScrollPending,
      loadingOlderRef,
      stickToBottom,
      sending,
    );
  }, [
    id,
    initialScrollPending,
    loadingOlderRef,
    scrollArea,
    scrollContent,
    stickToBottom,
    sending,
  ]);
  return { loadApprovals };
}
